import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, subDays, startOfDay, startOfMonth, endOfMonth, addDays, differenceInCalendarDays } from 'date-fns';
import { Calendar } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const RANGES = [
  { key: '1w', label: '1W', days: 7 },
  { key: '1m', label: '1M' },      // full current month
  { key: '3m', label: '3M', days: 90 },
];

const FULL_MINUTES = 45;
const opacityFor = (mins) => {
  if (!mins || mins <= 0) return 0;
  return Math.min(1, mins / FULL_MINUTES);
};

/**
 * Compact activity grid for Home.
 *  - 1W  → 7 boxes (Mon–Sun)
 *  - 1M  → all days of the current calendar month
 *  - 3M  → last 90 days
 * Opacity per box scales with minutes trained that day (45m = 100%).
 * The selected range is persisted per user via PUT /auth/me { activity_range }.
 */
export default function ActivityMini() {
  const { user, setUser } = useAuth();
  const initialRange = user?.activity_range || '1m';
  const [range, setRange] = useState(initialRange);
  const [heatmap, setHeatmap] = useState({}); // dateStr → minutes

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/progress/activity-heatmap?months=6`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data?.days || []).forEach((d) => { map[d.date] = d.duration_minutes || 0; });
        setHeatmap(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user || range === (user.activity_range || '1m')) return;
    axios.put(`${API_URL}/auth/me`, { activity_range: range }, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  // Build the list of dates for the selected range
  const cells = useMemo(() => {
    const today = startOfDay(new Date());
    let list = [];
    if (range === '1w') {
      // Mon-Sun of the current week
      const dow = (today.getDay() + 6) % 7; // 0=Mon
      const monday = addDays(today, -dow);
      list = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
    } else if (range === '1m') {
      const first = startOfMonth(today);
      const last = endOfMonth(today);
      const n = differenceInCalendarDays(last, first) + 1;
      list = Array.from({ length: n }, (_, i) => addDays(first, i));
    } else {
      list = Array.from({ length: 90 }, (_, i) => subDays(today, 89 - i));
    }
    return list.map((d) => ({
      date: d,
      key: format(d, 'yyyy-MM-dd'),
      minutes: heatmap[format(d, 'yyyy-MM-dd')] || 0,
      isToday: d.getTime() === today.getTime(),
      isFuture: d > today,
    }));
  }, [range, heatmap]);

  const totalWorkouts = cells.reduce((a, c) => a + (c.minutes > 0 ? 1 : 0), 0);
  const totalMinutes = cells.reduce((a, c) => a + c.minutes, 0);

  // Grid columns — 1W keeps the wider Mon–Sun strip. 1M and 3M share the same
  // small-cell density (15 cols) so the monthly view doesn't feel bloated.
  const cols = range === '1w' ? 7 : 15;
  // For 1W, cells fill the row width (aspect-square). 1M / 3M use fixed 16px
  // squares — same size regardless of range — so they read "at a glance".
  const fixedSquare = range !== '1w';
  const gridCols = fixedSquare
    ? { gridTemplateColumns: `repeat(${cols}, 16px)`, justifyContent: 'space-between' }
    : { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };

  // No leading blanks — 1M now flows chronologically in a compact grid instead
  // of the calendar-column layout that forced enlarged cells.
  const leadingBlanks = 0;

  return (
    <div className="mb-6" data-testid="home-activity-mini">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar size={16} weight="fill" className="text-userRed" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
            Activity
          </p>
        </div>
        <div className="flex gap-1" data-testid="activity-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`h-7 px-2.5 rounded-ios-pill text-[10px] uppercase tracking-[0.14em] font-black ${
                range === r.key ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/55'
              }`}
              data-testid={`activity-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ios-card p-4">
        <div className="flex items-baseline gap-3 mb-3">
          <p
            className="text-4xl font-black font-heading tabular-nums text-graphite leading-none"
            data-testid="activity-total-count"
          >
            {totalWorkouts}
          </p>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-userRed leading-none">
              Workouts
            </p>
            <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-graphite/50 mt-0.5 leading-none">
              {RANGES.find((r) => r.key === range)?.label}
              {totalMinutes > 0 && ` · ${Math.round(totalMinutes)}m`}
            </p>
          </div>
        </div>

        <div className="grid gap-[3px]" style={gridCols} data-testid="activity-cells">
          {Array.from({ length: leadingBlanks }).map((_, i) => (
            <span key={`blank-${i}`} className={fixedSquare ? '' : 'aspect-square'} aria-hidden />
          ))}
          {cells.map((c) => {
            const o = opacityFor(c.minutes);
            const style = c.isFuture
              ? { backgroundColor: 'rgba(0,0,0,0.03)' }
              : o > 0
                ? { backgroundColor: `rgba(192, 57, 47, ${Math.max(0.18, o).toFixed(2)})` }
                : { backgroundColor: 'rgba(25,28,33,0.08)' };
            return (
              <span
                key={c.key}
                title={`${format(c.date, 'MMM d')} · ${Math.round(c.minutes)} min`}
                className={`${fixedSquare ? 'w-4 h-4' : 'aspect-square'} rounded-[3px] ${c.isToday ? 'ring-1 ring-userRed' : ''}`}
                style={style}
                data-testid={`activity-cell-${c.key}`}
                data-minutes={Math.round(c.minutes)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
